import { Bone, Object3D } from "three";

import { EdgeStructure } from "../../lib/ik/metadata";
import {
  BoneStats,
  calculateBoneLengths,
  generateRigPoints,
  optimiseGlobalScale,
  overallBoneError,
} from "../../lib/misc/measure";

export const defaultScale = 3.28084;

export function scaleModelConnected(
  boneStats: BoneStats,
  model: Object3D,
  id: number
): boolean {
  const names = [
    "Neck",
    "_neckUpper",
    "_head",
    "RElbow",
    "LElbow",
    "RShoulder",
    "LShoulder",
    "_spineLower",
    "_waist",
    "RHip",
    "LHip",
    "RKnee",
    "LKnee",
  ];

  const components: { [key: string]: Bone } = {};

  for (const name of names) {
    const obj = model.getObjectByName(name);
    if (!obj) {
      console.error(`missing joint ${name} in model, setting default scale`);
      model.scale.set(defaultScale, defaultScale, defaultScale);
      return false;
    }
    if (obj.type == "Bone") {
      components[name] = obj as Bone;
    } else {
      console.error("invalid bone object", name);
    }
  }

  const { scale, error } = optimiseGlobalScale(model, boneStats);
  model.scale.set(scale, scale, scale);

  // measure model
  const modelPoints = generateRigPoints(model);
  const modelLengths = calculateBoneLengths(modelPoints);

  if (!modelLengths) {
    console.error("unable to measure model");
    return false;
  }

  // scale torso
  const ratios = {
    Spine: boneStats.getMean("Spine") / modelLengths.Spine,
    Back: boneStats.getMean("Back") / modelLengths.Back,
    Head: boneStats.getMean("Head") / modelLengths.Head,
    Pelvis: boneStats.getMean("Pelvis") / modelLengths.Pelvis,
    Legs:
      (boneStats.getMean("Hip") / modelLengths.Hip +
        boneStats.getMean("Knee") / modelLengths.Knee) /
      2,
    Arms:
      (boneStats.getMean("Shoulder") / modelLengths.Shoulder +
        boneStats.getMean("Elbow") / modelLengths.Elbow) /
      2,
  };

  // TEMP trial and error scalings
  ratios.Spine = 1;
  ratios.Arms = 0.85;
  ratios.Legs = 1;
  ratios.Back = 1.2;
  ratios.Head = 0.3;

  const preErrors = overallBoneError(model, boneStats);

  // scale torso vertically
  scaleBone(components._spineLower, ratios.Spine, ["y*"]);
  scaleBone(components.LShoulder, ratios.Spine, ["z/"]);
  scaleBone(components.RShoulder, ratios.Spine, ["z/"]);
  scaleBone(components._neckUpper, ratios.Spine, ["y/"]);

  // scale neck vertically
  scaleBone(components._neckUpper, ratios.Head, ["y*"]);
  scaleBone(components._head, ratios.Head, ["y/"]);

  // scale shoulders laterally
  scaleBone(components.LShoulder, ratios.Back, ["y*"]);
  scaleBone(components.RShoulder, ratios.Back, ["y*"]);
  scaleBone(components.LElbow, ratios.Back, ["y/"]);
  scaleBone(components.RElbow, ratios.Back, ["y/"]);

  // scale hips laterally
  scaleBone(components.LHip, ratios.Pelvis, ["y*"]);
  scaleBone(components.RHip, ratios.Pelvis, ["y*"]);
  scaleBone(components.LKnee, ratios.Pelvis, ["x/"]);
  scaleBone(components.RKnee, ratios.Pelvis, ["x/"]);

  // scale arms and legs
  scaleBone(components.RElbow, ratios.Arms, ["y*"]);
  scaleBone(components.LElbow, ratios.Arms, ["y*"]);
  scaleBone(components.RKnee, ratios.Legs, ["y*"]);
  scaleBone(components.LKnee, ratios.Legs, ["y*"]);

  // const postErrors = overallBoneError(model, boneStats);

  // modelPoints = generateRigPoints(model);
  // modelLengths = calculateBoneLengths(modelPoints);
  // console.log("lengths after", modelLengths);

  // console.log("data lengths", boneStats);

  // console.log("scaled with ratios", ratios);

  return true;
}

export function scaleModelRBI(
  boneStats: BoneStats,
  model: Object3D,
  structure: EdgeStructure,
  id: number
): boolean {
  const names = [
    "joint_Pelvis",
    "joint_TorsoB",
    "joint_ClavicleLT",
    "joint_ClavicleRT",
    "joint_Neck",
    "joint_Head",
    "joint_ShoulderLT",
    "joint_ShoulderRT",
    "joint_HipLT",
    "joint_HipRT",
  ];

  const components: { [key: string]: Bone } = {};

  for (const name of names) {
    const obj = model.getObjectByName(name) as Bone;
    if (!obj) {
      console.error(`missing joint ${name} in model, setting default scale`);
      model.scale.set(defaultScale, defaultScale, defaultScale);
      return false;
    }
    if (obj.isBone) {
      components[name] = obj;
    } else {
      console.error("invalid bone object", name);
    }
  }

  const optim = optimiseGlobalScale(model, boneStats, structure);
  model.scale.set(optim.scale, optim.scale, optim.scale);

  // const fudgeFactors: BoneRatios = {
  //   // Spine:  1,
  //   // Back:   1.1875,
  //   // Head:   2.56,
  //   // Pelvis: 1,
  //   // Legs:   0.93,
  //   // Arms:   0.72,
  //   Spine:  1,
  //   Back:   1,
  //   Head:   2.56,
  //   Pelvis: 1,
  //   Legs:   0.93,
  //   Arms:   1,
  // };

  // // scale torso
  // let ratios = getRatios(boneStats, model, structure, fudgeFactors);
  // if (!ratios) return false;

  // console.log("ratios:", ratios);

  // // TEMP trial and error scalings
  // ratios.Spine = 1.2;
  // // ratios.Arms = 0.85;
  // // ratios.Legs = 1;
  // // ratios.Back = 1.2;
  // // ratios.Head = 0.3;

  // const preErrors = overallBoneError(model, boneStats, undefined, structure);

  // // scale hips laterally
  // scaleBone(components.joint_Pelvis, ratios.Pelvis, ["x*", "y*", "z*"]);

  // ratios = getRatios(boneStats, model, structure, fudgeFactors);
  // if (!ratios) return false;

  // // scale torso vertically
  // // scaleBone(components.joint_Pelvis, ratios.Spine, ["y*"]);
  // // scaleBone(components.joint_ClavicleLT, ratios.Spine, ["z/"]);
  // // scaleBone(components.joint_ClavicleRT, ratios.Spine, ["z/"]);
  // // scaleBone(components.joint_Neck, ratios.Spine, ["y/"]);
  // // scaleBone(components.joint_HipLT, ratios.Spine, ["y/"]);
  // // scaleBone(components.joint_HipRT, ratios.Spine, ["y/"]);

  // // scale neck vertically
  // // scaleBone(components.joint_TorsoB, ratios.Head, ["y*"]);
  // // scaleBone(components.joint_Head, ratios.Head, ["z/"]);

  // // scale shoulders laterally
  // scaleBone(components.joint_TorsoB, ratios.Back, ["z*"]);
  // scaleBone(components.joint_Neck, ratios.Back, ["z/"]);
  // scaleBone(components.joint_ShoulderLT, ratios.Back, ["y/"]);
  // scaleBone(components.joint_ShoulderRT, ratios.Back, ["y/"]);

  // // scale arms and legs
  // scaleBone(components.joint_ShoulderLT, ratios.Arms, ["y*"]);
  // scaleBone(components.joint_ShoulderRT, ratios.Arms, ["y*"]);
  // scaleBone(components.joint_HipLT, ratios.Legs, ["y*"]);
  // scaleBone(components.joint_HipRT, ratios.Legs, ["y*"]);

  // const postErrors = overallBoneError(model, boneStats, undefined, structure);

  // console.log("preErrors", preErrors);
  // console.log("postErrors", postErrors);

  // const postOptim = optimiseGlobalScale(model, boneStats, structure);
  // model.scale.set(postOptim.scale, postOptim.scale, postOptim.scale);

  // const postPostErrors = overallBoneError(model, boneStats, undefined, structure);

  // console.log("postPostErrors", postPostErrors);

  // modelPoints = generateRigPoints(model);
  // modelLengths = calculateBoneLengths(modelPoints);
  // console.log("lengths after", modelLengths);

  // console.log("data lengths", boneStats);

  // console.log("scaled with ratios", ratios);

  return true;
}

function scaleBone(bone: Bone, ratio: number, apply: string[]) {
  const scale = bone.scale;

  if (apply.includes("x*")) {
    scale.setX(scale.x * ratio);
  }
  if (apply.includes("x/")) {
    scale.setX(scale.x / ratio);
  }

  if (apply.includes("y*")) {
    scale.setY(scale.y * ratio);
  }
  if (apply.includes("y/")) {
    scale.setY(scale.y / ratio);
  }

  if (apply.includes("z*")) {
    scale.setZ(scale.z * ratio);
  }
  if (apply.includes("z/")) {
    scale.setZ(scale.z / ratio);
  }
}

function getRatios(
  boneStats: BoneStats,
  model: Object3D,
  structure: EdgeStructure,
  fudgeFactors: BoneRatios
): BoneRatios | undefined {
  const modelPoints = generateRigPoints(model, structure);
  const modelLengths = calculateBoneLengths(modelPoints);

  console.log("riggedLengths", modelLengths);
  console.log("boneStats", boneStats);

  if (!modelLengths) {
    console.error("unable to measure model");
    return undefined;
  }

  const ratios: BoneRatios = {
    Spine:
      fudgeFactors.Spine * (boneStats.getMean("Spine") / modelLengths.Spine),
    Back: fudgeFactors.Back * (boneStats.getMean("Back") / modelLengths.Back),
    Head: fudgeFactors.Head * (boneStats.getMean("Head") / modelLengths.Head),
    Pelvis:
      fudgeFactors.Pelvis * (boneStats.getMean("Pelvis") / modelLengths.Pelvis),
    Legs:
      fudgeFactors.Legs *
      ((boneStats.getMean("Hip") / modelLengths.Hip +
        boneStats.getMean("Knee") / modelLengths.Knee) /
        2),
    Arms:
      fudgeFactors.Arms *
      ((boneStats.getMean("Shoulder") / modelLengths.Shoulder +
        boneStats.getMean("Elbow") / modelLengths.Elbow) /
        2),
  };

  return ratios;
}

interface BoneRatios {
  Spine: number;
  Back: number;
  Head: number;
  Pelvis: number;
  Legs: number;
  Arms: number;
}
